/*
 * Pure model for the player match sheet: which rows exist, and a one-line
 * highlight built only from verified numbers. Values that were not recorded
 * are omitted; they are never shown as zero.
 */

const fin = (v) => (v === null || v === undefined || v === "" || !Number.isFinite(Number(v)) ? null : Number(v));

/**
 * Merge order for the top stats, never mixing sources inside one number:
 *   1. API-Football game-log row for this match (public);
 *   2. PitchAPI single-match values (admin only; `pitch`), when there is no row;
 *   3. lineup event counts, only when the match has an event feed (`ev` is null otherwise).
 * Anything none of them recorded stays null and is omitted, never shown as 0.
 */
export function mergeMatchFacts({ row = null, ev = null, pitch = null }) {
  const pick = (rowVal, pitchVal, evVal) => {
    const r = fin(rowVal);
    if (r !== null) return r;
    if (!row && pitch && pitchVal !== null && pitchVal !== undefined) return pitchVal;
    return ev && evVal !== undefined ? evVal : null;
  };
  const fromPitch = !row && pitch && (pitch.goals !== null || pitch.assists !== null || pitch.minutes !== null);
  return {
    minutes: row ? fin(row.minutes) : pitch?.minutes ?? null,
    rating: fin(row?.rating),
    goals: pick(row?.goals, pitch?.goals, ev?.goals),
    assists: pick(row?.assists, pitch?.assists, ev?.assists),
    shots: row ? fin(row.shots) : pitch?.shots ?? null,
    shotsOnTarget: fin(row?.sot),
    yellow: pick(row?.yellow, pitch?.yellow, ev?.yellow),
    red: pick(row?.red, pitch?.red, ev?.red),
    foulsCommitted: fin(row?.fouls_committed),
    foulsDrawn: fin(row?.fouls_drawn),
    source: row ? "API-Football match log" : fromPitch ? (pitch.sourceLabel || "PitchAPI (private research)") : ev ? "Match events" : "",
  };
}

/**
 * PitchAPI values for ONE match (admin/private research only).
 *
 * The endpoint can return a multi-match sample (career/last-N) if the match
 * filter is ignored or the identity resolves differently. A match sheet must
 * never show that, so the payload is accepted only when every sampled match is
 * the requested one; heatmap and shots are then selected by fixture id too.
 */
export function pitchMatchFacts(payload, matchId) {
  if (!payload || payload.status !== "AVAILABLE" || !matchId) return null;
  const matches = Array.isArray(payload.matches) ? payload.matches : [];
  const wanted = String(matchId);
  if (!matches.length || !matches.every((m) => String(m.fixture_id) === wanted)) return null;
  const m = payload.metrics || {};
  const val = (k) => fin(m[k]?.value);
  const shots = (Array.isArray(payload.shots) ? payload.shots : []).filter((s) => String(s.fixture_id) === wanted);
  const xgValues = shots.map((s) => fin(s.xg)).filter((v) => v !== null);
  const heat = (Array.isArray(payload.heatmaps) ? payload.heatmaps : []).find((h) => String(h.fixture_id) === wanted) || null;
  return {
    sourceLabel: payload.rights === "COMMERCIAL_ANALYTICS_APPROVED" ? "PitchAPI (recorded match)" : "PitchAPI (private research)",
    passesAttempted: val("passes_attempted"),
    passesCompleted: val("accurate_passes"),
    chancesCreated: val("key_passes") ?? val("chances_created"),
    tackles: val("tackles") ?? val("matchstats.headers.tackles"),
    interceptions: val("interceptions"),
    recoveries: val("recoveries"),
    goals: val("goals"),
    assists: val("assists"),
    shots: val("total_shots"),
    yellow: val("yellow_cards"),
    red: val("red_cards"),
    minutes: fin(matches[0]?.minutes),
    xg: xgValues.length ? xgValues.reduce((x, y) => x + y, 0) : null,
    shotCount: shots.length,
    shotList: shots,
    all: Object.fromEntries(Object.keys(m).map((k) => [k, val(k)]).filter(([, v]) => v !== null)),
    heat,
  };
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** One sentence, only from recorded values. Returns "" when nothing is notable. */
export function deriveHighlight(facts, pitch = null) {
  if (!facts) return "";
  const { goals, assists, shots, shotsOnTarget, red, yellow, minutes } = facts;
  if (red > 0) return "Sent off.";
  if (goals > 0 && assists > 0) return `Scored ${plural(goals, "goal", "goals")} and provided ${plural(assists, "assist", "assists")}.`;
  if (goals > 1) return `Scored ${goals} goals.`;
  if (goals === 1) return shots >= 3 ? `Scored from ${shots} shots.` : "Scored a goal.";
  if (assists > 0) return `Provided ${plural(assists, "assist", "assists")}.`;
  if (pitch?.chancesCreated >= 3) return `Created ${pitch.chancesCreated} chances.`;
  if (shots >= 4) return `Had ${shots} shots${shotsOnTarget !== null ? ` (${shotsOnTarget} on target)` : ""}.`;
  if (yellow > 0) return "Booked.";
  if (minutes !== null && minutes >= 90) return "Played the full match.";
  return "";
}

/** Rows for the Top stats list; unavailable values are skipped entirely. */
export function buildSheetRows(facts, pitch) {
  const rows = [];
  const add = (label, value, digits) => {
    if (value === null || value === undefined) return;
    rows.push({ label, value: digits !== undefined ? Number(value).toFixed(digits) : String(value) });
  };
  add("Minutes played", facts?.minutes);
  add("Goals", facts?.goals);
  add("Assists", facts?.assists);
  add("Shots", facts?.shots);
  add("Shots on target", facts?.shotsOnTarget);
  add("Expected goals (xG)", pitch?.xg ?? null, 2);
  add("Passes attempted", pitch?.passesAttempted);
  add("Passes completed", pitch?.passesCompleted);
  add("Chances created", pitch?.chancesCreated);
  add("Tackles", pitch?.tackles);
  add("Interceptions", pitch?.interceptions);
  add("Recoveries", pitch?.recoveries);
  // The provider logs 0 for both "none" and "not recorded", so only non-zero fouls are shown.
  add("Fouls committed", facts?.foulsCommitted > 0 ? facts.foulsCommitted : null);
  add("Fouls drawn", facts?.foulsDrawn > 0 ? facts.foulsDrawn : null);
  add("Yellow cards", facts?.yellow !== null && facts?.yellow > 0 ? facts.yellow : null);
  add("Red cards", facts?.red !== null && facts?.red > 0 ? facts.red : null);
  return rows;
}

/** Substitution context from the lineup alone, e.g. "Started · off 59'" . */
export function participationText(ev, isStarter) {
  if (!ev && isStarter === undefined) return "";
  const bits = [isStarter ? "Started" : "Substitute"];
  if (ev?.onAt != null) bits[0] = `On ${ev.onAt}'`;
  if (ev?.offAt != null) bits.push(`Off ${ev.offAt}'`);
  return bits.join(" · ");
}

/*
 * Every clearly defined per-match PitchAPI value, in FotMob-style groups. The
 * API only exposes a whitelist today (see the backend note); anything it does
 * expose appears automatically and anything it does not is simply absent.
 * Deliberately skipped because their meaning is not defined by the provider
 * feed: rating_title, shot_accuracy, big_chance_created_team_title and
 * matchstats headers other than tackles. xg_and_xa is derived (xG + xA).
 */
export const STAT_GROUPS = [
  { title: "Attack", rows: [
    ["touches", "Touches"], ["touches_opp_box", "Touches in opposition box"], ["ShotsOnTarget", "Shots on target"],
    ["ShotsOffTarget", "Shots off target"], ["npxg", "Non-penalty xG"], ["expected_goals_on_target_variant", "xG on target (xGOT)"],
    ["expected_assists", "Expected assists (xA)"], ["dribbles_succeeded", "Successful dribbles"], ["dispossessed", "Dispossessed"],
    ["Offsides", "Offsides"],
  ] },
  { title: "Passing", rows: [
    ["passes_into_final_third", "Passes into final third"], ["accurate_crosses", "Accurate crosses"], ["long_balls_accurate", "Accurate long balls"],
  ] },
  { title: "Defence", rows: [
    ["shot_blocks", "Blocks"], ["clearances", "Clearances"], ["dribbled_past", "Dribbled past"], ["defensive_actions", "Defensive actions"],
  ] },
  { title: "Duels", rows: [
    ["duel_won", "Duels won"], ["duel_lost", "Duels lost"], ["ground_duels_won", "Ground duels won"], ["aerials_won", "Aerial duels won"],
    ["was_fouled", "Was fouled"],
  ] },
];

const SHOWN_IN_TOP = new Set(["goals", "assists", "total_shots", "expected_goals", "passes_attempted", "accurate_passes", "chances_created", "key_passes", "tackles", "interceptions", "recoveries", "fouls", "minutes_played", "matchstats.headers.tackles"]);

/** Extra grouped sections from every recorded PitchAPI value not already in Top stats. */
export function buildSheetSections(pitch) {
  const all = pitch?.all || {};
  const num = (v) => (Number.isInteger(v) ? String(v) : Number(v).toFixed(2));
  return STAT_GROUPS.map((g) => ({
    title: g.title,
    rows: g.rows.filter(([k]) => all[k] !== undefined && !SHOWN_IN_TOP.has(k)).map(([k, label]) => ({ key: k, label, value: num(all[k]) })),
  })).filter((g) => g.rows.length);
}
